import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Fade,
  keyframes,
} from '@mui/material';
import {
  Close,
  ArrowForward,
  ArrowBack,
  Home,
  Assessment,
  ForkRight,
  Merge,
  Commit,
  LocalOffer,
  NewReleases,
  Flag,
  TrendingUp,
  Warning,
  Psychology,
  CheckCircle,
  Speed,
  Description,
  Architecture,
  Layers,
} from '@mui/icons-material';
import {
  BranchingBasicsDiagram,
  MergeDiagram,
  BranchWorkflowDiagram,
  ConflictDiagram,
} from './BranchDiagrams';
import { useOnboarding, TUTORIAL_PHASES } from '../../context/OnboardingContext';
import {
  cad_tutorial, chat_tutorial, requirements_tutorial,
  documents_tutorial, compliance_tutorial, verification_tutorial,
  knowledge_base_tutorial, orchestrator_tutorial,
} from './sectionTutorialSlides';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.5); }
  70% { box-shadow: 0 0 0 14px rgba(102, 126, 234, 0); }
  100% { box-shadow: 0 0 0 0 rgba(102, 126, 234, 0); }
`;

// ═══════════════════════════════════════════════════════════════
// SLIDE CONTENT — grouped by slideGroup for each educate phase
// ═══════════════════════════════════════════════════════════════

const SLIDE_GROUPS = {
  home: [
    {
      title: 'Your Project Hub',
      icon: <Home sx={{ fontSize: 28 }} />,
      color: '#667eea',
      content: (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            Welcome to your project! The <strong>Home</strong> tab is where you configure everything and manage your engineering workflow.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FeatureCard icon={<Home />} title="Home" desc="Project details, branches, milestones, version control" color="#667eea" />
            <FeatureCard icon={<Assessment />} title="Analytics" desc="Scorecards, KPIs, trends, risks, AI reports" color="#764ba2" />
          </Box>
        </>
      ),
    },
    {
      title: 'Project Details',
      icon: <Description sx={{ fontSize: 28 }} />,
      color: '#667eea',
      content: (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            All fields are <strong>editable inline</strong>. The "Objectives" field is especially important — e-pilot reads it to tailor AI responses.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <InfoItem label="Project Name & Status" desc="Active, On Hold, Completed, Archived" />
            <InfoItem label="Engineering Domain" desc="Railway, Optoelectronics, etc." />
            <InfoItem label="Objectives & Context" desc="e-pilot uses this for smarter answers" />
            <InfoItem label="Owner & Department" desc="Project ownership and team" />
          </Box>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(102,126,234,0.06)', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Next up:</strong> You'll create your first branch — let's learn how branching works!
            </Typography>
          </Box>
        </>
      ),
    },
  ],

  branches: [
    {
      title: 'What Are Branches?',
      icon: <ForkRight sx={{ fontSize: 28 }} />,
      color: '#11998e',
      content: (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
            Branches let your team work on different things <strong>at the same time</strong> without interfering with each other.
          </Typography>
          <BranchingBasicsDiagram />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
            <strong style={{ color: '#667eea' }}>main</strong> is your stable baseline.
            <strong style={{ color: '#11998e' }}> Feature branches</strong> are where new work happens.
          </Typography>
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1.5 }}>
            <MiniCard color="#667eea" label="main" desc="Stable, released work" />
            <MiniCard color="#11998e" label="dev" desc="Integration branch" />
            <MiniCard color="#764ba2" label="feature/*" desc="Workstreams" />
          </Box>
        </>
      ),
    },
    {
      title: 'Merging Branches',
      icon: <Merge sx={{ fontSize: 28 }} />,
      color: '#ff9800',
      content: (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
            When work is done, you <strong>merge</strong> it back into main. This combines all changes into one version.
          </Typography>
          <MergeDiagram />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
            <strong>Next:</strong> You'll merge the branch you just created. Click "Merge Branches" and select your branch as the source and main as the target.
          </Typography>
        </>
      ),
    },
    {
      title: 'Handling Conflicts',
      icon: <Warning sx={{ fontSize: 28 }} />,
      color: '#f44336',
      content: (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
            When two branches modify the same thing, EIS detects a <strong>conflict</strong> and shows both versions side-by-side.
          </Typography>
          <ConflictDiagram />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
            The <strong>Conflict Resolver</strong> lets you pick "theirs", "ours", or write a custom resolution.
          </Typography>
        </>
      ),
    },
    {
      title: 'The Full Workflow',
      icon: <Layers sx={{ fontSize: 28 }} />,
      color: '#667eea',
      content: (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
            Here's how the pieces fit together:
          </Typography>
          <BranchWorkflowDiagram />
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <WorkflowStep num="1" text="Create your project — a main branch is set up automatically" />
            <WorkflowStep num="2" text="Create feature branches for workstreams" />
            <WorkflowStep num="3" text="Upload files, create requirements, add documents" />
            <WorkflowStep num="4" text="Review changes and merge back when ready" />
            <WorkflowStep num="5" text="Tag a version and create a release" />
          </Box>
        </>
      ),
    },
  ],

  version_control: [
    {
      title: 'Commits, Tags & Releases',
      icon: <Commit sx={{ fontSize: 28 }} />,
      color: '#764ba2',
      content: (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            The <strong>Version Control Panel</strong> below has three tabs:
          </Typography>
          <FeatureRow icon={<Commit />} title="Commits" color="#764ba2"
            items={['Every change is staged automatically', 'Write a commit message to save a snapshot', 'View history, revert mistakes, or cherry-pick changes']} />
          <Box sx={{ mt: 1 }} />
          <FeatureRow icon={<LocalOffer />} title="Tags" color="#667eea"
            items={['Mark important commits with a version label (v1.0, CDR-ready)', 'Tags permanently point to the exact state at that moment']} />
          <Box sx={{ mt: 1 }} />
          <FeatureRow icon={<NewReleases />} title="Releases" color="#11998e"
            items={['Bundle a tagged version into a formal release', 'Share deliverables with clients or reviewers']} />
        </>
      ),
    },
    {
      title: 'Milestones & Pending Changes',
      icon: <Flag sx={{ fontSize: 28 }} />,
      color: '#667eea',
      content: (
        <>
          <FeatureRow icon={<Flag />} title="Milestones" color="#667eea"
            items={['Define milestones with target dates and status', 'Track Pending → In Progress → Completed', 'Overdue milestones are flagged automatically', 'e-pilot uses milestones to understand project urgency']} />
          <Box sx={{ mt: 1.5 }} />
          <FeatureRow icon={<Layers />} title="Pending Changes" color="#764ba2"
            items={['Shows uncommitted changes on your current branch', 'Stage or unstage individual files', 'Write a commit message and save your work']} />
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(102,126,234,0.06)', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Next:</strong> Let's explore the Analytics tab — click it to continue!
            </Typography>
          </Box>
        </>
      ),
    },
  ],

  analytics: [
    {
      title: 'Project Scorecard & KPIs',
      icon: <Speed sx={{ fontSize: 28 }} />,
      color: '#764ba2',
      content: (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            The <strong>Analytics</strong> tab gives you a data-driven view of project health:
          </Typography>
          <FeatureRow icon={<Speed />} title="Scorecard" color="#764ba2"
            items={['Auto-computed 0-100 score based on 6 weighted categories', 'Requirements 25%, Documents 20%, CAD 15%, VC 15%, Milestones 15%, Compliance 10%', 'Grade A-F with donut gauge']} />
          <Box sx={{ mt: 1 }} />
          <FeatureRow icon={<CheckCircle />} title="KPIs & Gate Review" color="#11998e"
            items={['Requirements, Documents, CAD — count + status breakdown', 'Coverage bars: % verified, % released, % ready', 'AI-powered gate review: GO / NO-GO / CONDITIONAL']} />
        </>
      ),
    },
    {
      title: 'Intelligence, Risks & AI Health',
      icon: <TrendingUp sx={{ fontSize: 28 }} />,
      color: '#11998e',
      content: (
        <>
          <FeatureRow icon={<TrendingUp />} title="Intelligence" color="#11998e"
            items={['Week-over-week metrics with trend arrows and delta %', 'Commits, docs, requirements, CAD, daily activity', 'Progression charts (bar, line) with adjustable time range']} />
          <Box sx={{ mt: 1 }} />
          <FeatureRow icon={<Warning />} title="Risks & Attention" color="#f44336"
            items={['Failed requirements, stale branches (14+ days idle)', 'Stuck drafts (30+ days), open merge requests (7+ days)']} />
          <Box sx={{ mt: 1 }} />
          <FeatureRow icon={<Psychology />} title="AI Health Summary" color="#764ba2"
            items={['AI analyzes all project data and generates a health report', 'Risk level: On Track / Needs Attention / Critical', 'Prioritized recommendations with specific actions']} />
        </>
      ),
    },
  ],

  // Section tutorials (imported from sectionTutorialSlides.js)
  cad_tutorial,
  chat_tutorial,
  requirements_tutorial,
  documents_tutorial,
  compliance_tutorial,
  verification_tutorial,
  knowledge_base_tutorial,
  orchestrator_tutorial,
};

// ═══════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════

function FeatureCard({ icon, title, desc, color }) {
  return (
    <Box sx={{ flex: 1, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0.5 }}>
      <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="caption" color="text.secondary">{desc}</Typography>
    </Box>
  );
}

function MiniCard({ color, label, desc }) {
  return (
    <Box sx={{ flex: 1, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: `${color}10`, borderLeft: `3px solid ${color}` }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color }}>{label}</Typography>
      <Typography variant="caption" display="block" color="text.secondary">{desc}</Typography>
    </Box>
  );
}

function InfoItem({ label, desc }) {
  return (
    <Box sx={{ p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>{label}</Typography>
      <Typography variant="caption" display="block" color="text.secondary">{desc}</Typography>
    </Box>
  );
}

function FeatureRow({ icon, title, color, items }) {
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      </Box>
      {items.map((item, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, ml: 0.5, mt: 0.3 }}>
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: color, mt: 0.9, flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>{item}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function WorkflowStep({ num, text }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: 'rgba(102,126,234,0.1)', color: '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
        {num}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>{text}</Typography>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════
// TUTORIAL ACTION SPOTLIGHT — pulsing ring + tooltip for action phases
// ═══════════════════════════════════════════════════════════════

function TutorialSpotlight({ phase, onSkip }) {
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const pollRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!phase?.target) return;
    let attempts = 0;
    const find = () => {
      const el = document.querySelector(phase.target);
      if (el) {
        clearInterval(pollRef.current);
        const rect = el.getBoundingClientRect();
        setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
        setVisible(true);
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else if (++attempts >= 50) {
        clearInterval(pollRef.current);
      }
    };
    pollRef.current = setInterval(find, 100);
    find();
    return () => { clearInterval(pollRef.current); cancelAnimationFrame(rafRef.current); };
  }, [phase]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!visible || !phase?.target) return;
    const reposition = () => {
      const el = document.querySelector(phase.target);
      if (el) {
        const rect = el.getBoundingClientRect();
        setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
      }
    };
    const throttled = () => { cancelAnimationFrame(rafRef.current); rafRef.current = requestAnimationFrame(reposition); };
    window.addEventListener('scroll', throttled, true);
    window.addEventListener('resize', throttled);
    const interval = setInterval(reposition, 1000);
    return () => { window.removeEventListener('scroll', throttled, true); window.removeEventListener('resize', throttled); clearInterval(interval); };
  }, [visible, phase]);

  if (!phase || !targetRect || !visible) return null;

  const pad = 8;
  const tooltipBelow = targetRect.top + targetRect.height + pad + 12;
  const flipUp = tooltipBelow > window.innerHeight - 180;
  const tooltipLeft = Math.min(Math.max(16, targetRect.left), window.innerWidth - 320);

  // Find phase index for display
  const phaseNum = TUTORIAL_PHASES.findIndex(p => p.id === phase.id) + 1;

  return (
    <>
      {/* Pulsing ring */}
      <Fade in>
        <Box sx={{
          position: 'fixed', top: targetRect.top - pad, left: targetRect.left - pad,
          width: targetRect.width + pad * 2, height: targetRect.height + pad * 2,
          borderRadius: 2, border: '2px solid #667eea', animation: `${pulse} 2s infinite`,
          zIndex: 1301, pointerEvents: 'none',
        }} />
      </Fade>

      {/* Instruction tooltip */}
      <Fade in>
        <Paper elevation={12} sx={{
          position: 'fixed',
          top: flipUp ? undefined : tooltipBelow,
          bottom: flipUp ? window.innerHeight - targetRect.top + pad + 12 : undefined,
          left: tooltipLeft, zIndex: 1302, maxWidth: 300, borderRadius: 2, overflow: 'hidden',
        }}>
          <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
              Step {phaseNum} of {TUTORIAL_PHASES.length}
            </Typography>
          </Box>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.5 }}>
              {phase.hint}
            </Typography>
          </Box>
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onSkip} sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem' }}>
              Skip tutorial
            </Button>
          </Box>
        </Paper>
      </Fade>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// TUTORIAL EDUCATION DIALOG — slides shown between actions
// ═══════════════════════════════════════════════════════════════

function TutorialDialog({ slideGroup, open, onDone, onSkip }) {
  const [slideIndex, setSlideIndex] = useState(0);
  const slides = SLIDE_GROUPS[slideGroup] || [];
  const slide = slides[slideIndex];
  const isLast = slideIndex === slides.length - 1;

  // Reset slide index when group changes
  useEffect(() => { setSlideIndex(0); }, [slideGroup]);

  if (!slide || !open) return null;

  return (
    <Dialog open={open} onClose={onSkip} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', maxHeight: '85vh' } }}>
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${slide.color} 0%, ${slide.color}cc 100%)`,
        px: 3, pt: 2.5, pb: 2, transition: 'background 0.4s',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ color: 'rgba(255,255,255,0.9)' }}>{slide.icon}</Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
              {slide.title}
            </Typography>
          </Box>
          <IconButton onClick={onSkip} sx={{ color: 'white', mt: -0.5 }}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <DialogContent sx={{ px: 3, py: 2.5, overflow: 'auto' }}>
        {slide.content}
      </DialogContent>

      {/* Footer */}
      <Box sx={{
        px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {slides.map((_, i) => (
            <Box key={i} sx={{
              width: slideIndex === i ? 16 : 6, height: 6, borderRadius: 3,
              bgcolor: slideIndex === i ? slide.color : `${slide.color}30`,
              transition: 'all 0.3s', cursor: 'pointer',
            }} onClick={() => setSlideIndex(i)} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {slideIndex > 0 && (
            <Button size="small" startIcon={<ArrowBack sx={{ fontSize: 14 }} />}
              onClick={() => setSlideIndex(p => p - 1)}
              sx={{ textTransform: 'none', color: 'text.secondary' }}>
              Back
            </Button>
          )}
          <Button size="small" onClick={onSkip} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Skip tutorial
          </Button>
          {isLast ? (
            <Button size="small" variant="contained"
              endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
              onClick={onDone}
              sx={{ textTransform: 'none', background: `linear-gradient(135deg, ${slide.color} 0%, ${slide.color}cc 100%)` }}>
              Continue
            </Button>
          ) : (
            <Button size="small" variant="contained"
              endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
              onClick={() => setSlideIndex(p => p + 1)}
              sx={{ textTransform: 'none', background: `linear-gradient(135deg, ${slide.color} 0%, ${slide.color}cc 100%)` }}>
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT — orchestrates spotlight + dialog phases
// ═══════════════════════════════════════════════════════════════

export default function ProjectTutorial() {
  const {
    tutorialActive, currentTutorialPhase,
    showTutorialDialog, advanceTutorial, skipTutorial,
    sectionTutorial, setSectionTutorial,
  } = useOnboarding();

  // Section tutorial (shown after completing any non-project onboarding step)
  if (sectionTutorial) {
    return (
      <TutorialDialog
        slideGroup={sectionTutorial}
        open={true}
        onDone={() => setSectionTutorial(null)}
        onSkip={() => setSectionTutorial(null)}
      />
    );
  }

  // Project tutorial (interactive phase-based walkthrough)
  if (!tutorialActive || !currentTutorialPhase) return null;

  if (currentTutorialPhase.type === 'action') {
    return <TutorialSpotlight phase={currentTutorialPhase} onSkip={skipTutorial} />;
  }

  if (currentTutorialPhase.type === 'educate') {
    return (
      <TutorialDialog
        slideGroup={currentTutorialPhase.slideGroup}
        open={showTutorialDialog}
        onDone={advanceTutorial}
        onSkip={skipTutorial}
      />
    );
  }

  return null;
}
